import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';
import Select from '../../components/Select';
import Field from '../../components/ui/Field';
import Estado from '../../components/ui/Estado';
import EmptyState from '../../components/ui/EmptyState';
import PageHeader from '../../components/ui/PageHeader';
import BarChart from '../../components/ui/BarChart';
import DataTable from '../../components/ui/DataTable';
import Metrica, { MetricaGrid } from '../../components/ui/Metrica';
import { fmt, fmtBRL, fmtPct } from '../../utils/format';
import { CORES_GRAFICO, classeSinal, tomSinal } from '../../utils/graficos';

export default function CompararSafras() {
  const [safras, setSafras] = useState([]);
  const [cultura, setCultura] = useState('');
  const [selecionadas, setSelecionadas] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [erroSafras, setErroSafras] = useState('');

  useEffect(() => {
    api.getSafras()
      .then((lista) => setSafras(lista || []))
      .catch((e) => setErroSafras(e.message || 'Erro ao carregar safras.'));
  }, []);

  const culturas = useMemo(() => {
    const set = new Set();
    safras.forEach((s) => {
      const c = (s.cultura || '').trim();
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [safras]);

  useEffect(() => {
    if (!cultura && culturas.length) setCultura(culturas[0]);
  }, [culturas, cultura]);

  const safrasDaCultura = useMemo(() => {
    if (!cultura) return [];
    return safras
      .filter((s) => (s.cultura || '').trim() === cultura)
      .slice()
      .reverse();
  }, [safras, cultura]);

  // Default: até 4 safras mais recentes da cultura
  useEffect(() => {
    if (!cultura) {
      setSelecionadas([]);
      setResultados([]);
      return;
    }
    const ids = safras
      .filter((s) => (s.cultura || '').trim() === cultura)
      .slice()
      .reverse()
      .slice(0, 4)
      .map((s) => s.id);
    setSelecionadas(ids);
    setResultados([]);
  }, [cultura, safras]);

  useEffect(() => {
    if (selecionadas.length === 0) {
      setResultados([]);
      return;
    }
    let vivo = true;
    setCarregando(true);
    setErro('');
    Promise.all(selecionadas.map((id) => api.getResultadoSafra(id)))
      .then((lista) => {
        if (!vivo) return;
        setResultados(lista || []);
      })
      .catch((e) => {
        if (!vivo) return;
        setErro(e.message || 'Não foi possível carregar os resultados.');
        setResultados([]);
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => { vivo = false; };
  }, [selecionadas]);

  const toggleSafra = (id) => {
    setSelecionadas((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const barras = (getter, formatValue) => (
    <BarChart
      data={resultados.map((r, i) => ({
        label: r.safraDescricao || `Safra ${r.safraId}`,
        value: getter(r) ?? 0,
        color: CORES_GRAFICO[i % CORES_GRAFICO.length],
      }))}
      formatValue={formatValue}
      height={28}
      labelWidth={120}
    />
  );

  const lucroTotal = resultados.reduce((s, r) => s + (r.resultadoBrutoBRL || 0), 0);
  const melhor = resultados.reduce((acc, r) => {
    if (r.resultadoBrutoBRL == null) return acc;
    if (!acc || r.resultadoBrutoBRL > acc.resultadoBrutoBRL) return r;
    return acc;
  }, null);

  const msgVazio = (() => {
    if (carregando || erro) return null;
    if (culturas.length === 0) {
      return {
        titulo: 'Cadastre safras com cultura',
        descricao: 'Informe a cultura no cadastro de safras para comparar ciclos da mesma cultura.',
      };
    }
    if (safrasDaCultura.length < 2) {
      return {
        titulo: 'Não há duas safras da mesma cultura para comparar',
        descricao: cultura
          ? `Só existe ${safrasDaCultura.length === 1 ? 'uma safra' : 'nenhuma safra'} de ${cultura}. Cadastre outra safra com a mesma cultura para comparar.`
          : 'Cadastre pelo menos duas safras com a mesma cultura.',
      };
    }
    if (selecionadas.length < 2 || resultados.length < 2) {
      return {
        titulo: 'Selecione ao menos duas safras',
        descricao: 'Marque duas ou mais safras da mesma cultura acima para montar os gráficos.',
      };
    }
    return null;
  })();

  return (
    <div>
      <PageHeader
        titulo="Comparar safras"
        icone="comparar-safras"
        descricao="Safras da mesma cultura — produtividade, custo, lucro e margem."
        voltar={{ to: '/relatorios', label: 'Relatórios' }}
      >
        <div className="filtros">
          <Field label="Cultura" obrigatorio>
            <Select
              value={cultura}
              onChange={setCultura}
              options={culturas.map((c) => ({ value: c, label: c }))}
              placeholder={erroSafras || (culturas.length ? 'Selecione' : 'Nenhuma cultura cadastrada')}
              disabled={!culturas.length}
            />
          </Field>
        </div>

        {safrasDaCultura.length >= 2 && (
          <div className="chips">
            <span className="chips-label">Safras na comparação (mín. 2)</span>
            {safrasDaCultura.map((s) => {
              const on = selecionadas.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`chip ${on ? 'chip--ativo' : ''}`}
                  onClick={() => toggleSafra(s.id)}
                >
                  {s.descricao}
                </button>
              );
            })}
          </div>
        )}
      </PageHeader>

      <Estado carregando={carregando} erro={erro} itens={4}>
        {msgVazio ? (
          <EmptyState
            compacto
            icone="comparar-safras"
            titulo={msgVazio.titulo}
            descricao={msgVazio.descricao}
            acao={safrasDaCultura.length < 2 || culturas.length === 0
              ? { to: '/safras', label: 'Ir para Safras' }
              : undefined}
          />
        ) : (
          <>
            <MetricaGrid min={140}>
              <Metrica label="Safras" value={String(resultados.length)} />
              <Metrica
                label="Melhor lucro"
                value={melhor ? fmtBRL(melhor.resultadoBrutoBRL) : '—'}
                detalhe={melhor?.safraDescricao}
                tom={melhor ? tomSinal(melhor.resultadoBrutoBRL) : undefined}
                destaque
              />
              <Metrica
                label="Lucro somado"
                value={fmtBRL(lucroTotal)}
                tom={tomSinal(lucroTotal)}
                destaque
              />
            </MetricaGrid>

            <div className="grade-graficos">
              <div className="card">
                <h2>Resultado bruto</h2>
                {barras((r) => r.resultadoBrutoBRL, fmtBRL)}
              </div>
              <div className="card">
                <h2>Custo total</h2>
                {barras((r) => r.custoTotalInsumosBRL, fmtBRL)}
              </div>
              <div className="card">
                <h2>Lucro por hectare</h2>
                {barras((r) => r.resultadoPorHectareBRL, fmtBRL)}
              </div>
              <div className="card">
                <h2>Margem bruta</h2>
                {barras((r) => r.margemBrutaPct, (v) => fmtPct(v))}
              </div>
              <div className="card">
                <h2>Produção (unidade principal)</h2>
                {barras((r) => r.totalProduzido, (v) => fmt(v))}
              </div>
            </div>

            <div className="card">
              <h2>Resumo</h2>
              <DataTable
                linhas={resultados}
                chaveDe={(r) => r.safraId}
                colunas={[
                  {
                    chave: 'nome',
                    titulo: 'Safra',
                    papel: 'titulo',
                    render: (r) => r.safraDescricao,
                  },
                  {
                    chave: 'area',
                    titulo: 'Área (ha)',
                    render: (r) => (r.areaHectaresTotal != null ? fmt(r.areaHectaresTotal) : '—'),
                  },
                  {
                    chave: 'prod',
                    titulo: 'Produção',
                    render: (r) =>
                      r.totalProduzido != null
                        ? `${fmt(r.totalProduzido)} ${r.unidadeProducao || ''}`.trim()
                        : '—',
                  },
                  {
                    chave: 'custo',
                    titulo: 'Custo',
                    render: (r) => fmtBRL(r.custoTotalInsumosBRL),
                  },
                  {
                    chave: 'lucro',
                    titulo: 'Lucro',
                    papel: 'destaque',
                    render: (r) => (
                      <span className={classeSinal(r.resultadoBrutoBRL)} style={{ fontWeight: 700 }}>
                        {fmtBRL(r.resultadoBrutoBRL)}
                      </span>
                    ),
                  },
                  {
                    chave: 'margem',
                    titulo: 'Margem',
                    render: (r) =>
                      r.margemBrutaPct != null ? fmtPct(r.margemBrutaPct) : '—',
                  },
                ]}
              />
            </div>
          </>
        )}
      </Estado>
    </div>
  );
}
